import {gql} from '@apollo/client';
import {StyleSheet, css} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import isArchived from 'archive/isArchived';
import CreateFormModal from 'forms/form-definition/CreateFormModal';
import {InitializeFormDesignerWithFormActionQuery} from 'forms/form-designer/graphql/__generated/InitializeFormDesignerWithFormActionQuery';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {TemplateCollectibleNameCell} from 'notebook/entry-templates/collections/TemplateCollectionTable';
import notifications from 'notifications';
import {ProcessTaskSchema} from 'processes/__generated/ProcessTaskSchema';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import EditableItemTable, {EditableItemRowProps} from 'ui-library/EditableItemTable';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import PlainSearchInput, {BorderRadius} from 'ui-library/PlainSearchInput';
import {readableTime} from 'util/DateUtil';
import sortedBy from 'util/sortedBy';
import strUtil from 'util/strUtil';

import {
  ProcessTaskSchemaFormsQuery,
  ProcessTaskSchemaFormsQueryVariables,
} from './__generated/ProcessTaskSchemaFormsQuery';

type FormDefinitionNode = ExtractByTypename<
  ExtractByTypename<
    ProcessTaskSchemaFormsQuery['processTaskSchema'],
    'ProcessTaskSchemaNode'
  >['forms'][number],
  'FormDefinitionNode'
>;

type InitialProcessTaskSchema =
  InitializeFormDesignerWithFormActionQuery['processTaskSchemasSupportingPlatformFeature'][number];

const MODAL_CONTAINER_KEY = 'ProcessTaskSchemaFormsList';

type ProcessTaskSchemaFormsListCells = [
  name: JSX.Element,
  type: string,
  author: string,
  createdAt: string,
  updatedAt: string,
];

const columnHeaders: Array<{name: string; width: number}> = [
  {name: 'Name', width: 4},
  {name: 'Type', width: 2},
  {name: 'Author', width: 2},
  {name: 'Created', width: 2},
  {name: 'Last Modified', width: 2},
];

export const PROCESS_TASK_SCHEMA_FORMS_QUERY = gql`
  query ProcessTaskSchemaFormsQuery($schemaId: StrictString!) @skipAudit @engComponent(name: FORMS) {
    processTaskSchema(id: $schemaId) {
      ... on ProcessTaskSchemaNode {
        id
        forms {
          ...InaccessibleInfoGraphQL
          ... on FormDefinitionNode {
            id
            name
            creator {
              id
              name
            }
            createdAt
            modifiedAt
            editUrl
            archiveRecord {
              purpose
            }
            isValid
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ProcessTaskSchemaFormsListProps {
  schemaId: string;
  processTaskSchema: ProcessTaskSchema;
}

export default function ProcessTaskSchemaFormsList({
  schemaId,
  processTaskSchema,
}: ProcessTaskSchemaFormsListProps): JSX.Element | null {
  const result = useBenchlingQuery<ProcessTaskSchemaFormsQuery, ProcessTaskSchemaFormsQueryVariables>(
    PROCESS_TASK_SCHEMA_FORMS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
      variables: {
        schemaId,
      },
    }
  );
  const [searchQuery, setSearchQuery] = useState<string | null>(null);

  const forms: Array<FormDefinitionNode> = useMemo(() => {
    if (result.type !== QueryResultType.SUCCESS) {
      return [];
    }
    const rawForms = !isInaccessible(result.data?.processTaskSchema)
      ? accessibleObjs(result.data?.processTaskSchema.forms)
      : [];

    return sortedBy(rawForms, [(form) => form.name.toLowerCase()]);
  }, [result]);

  const filteredForms: Array<FormDefinitionNode> = forms.filter(
    (form) =>
      // search
      !searchQuery || strUtil.caseInsensitiveContains(form.name, searchQuery)
  );

  const rows: Array<EditableItemRowProps> = filteredForms.map((form, index) => {
    const values: ProcessTaskSchemaFormsListCells = [
      <TemplateCollectibleNameCell templateCollectibleInfo={form} markNeedsUpdate={!form.isValid} />,
      'Task creation',
      form.creator.name,
      readableTime(form.createdAt, {showTime: false}),
      readableTime(form.modifiedAt, {showTime: false}),
    ];
    return {
      id: form.id,
      itemIndex: index,
      isArchived: isArchived(form),
      values,
    };
  });

  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load forms');
    return null;
  }

  function onClickCreateForm(): void {
    LegacyModalContainer.open(
      MODAL_CONTAINER_KEY,
      <CreateFormModal
        onRequestClose={() => {
          LegacyModalContainer.close(MODAL_CONTAINER_KEY);
        }}
        initialTaskSchema={processTaskSchema as unknown as InitialProcessTaskSchema}
      />
    );
  }

  return (
    <div className={css(styles.list)}>
      <EditableItemTable
        columnHeaders={columnHeaders}
        emptyMessage="No forms found for this schema"
        isLoading={result.type === QueryResultType.PENDING}
        rows={rows}
        getEditUrl={(index) => filteredForms[index].editUrl}
        writable={true}
        leftSubheaderNode={
          <PlainSearchInput
            className={css(styles.searchInput)}
            borderRadius={BorderRadius.LARGE}
            placeholder="Search"
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchQuery(e.target.value);
            }}
            onEscape={() => {
              setSearchQuery(null);
            }}
          />
        }
        rightSubheaderNode={
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.MEDIUM}
            text="Create"
            onClick={onClickCreateForm}
          />
        }
      />
    </div>
  );
}

const styles = StyleSheet.create({
  searchInput: {
    width: 200,
  },
  list: {
    ...FONT_PROXIMA_13,
  },
});
